import React, { useCallback } from 'react'
import { useEventListener } from 'usehooks-ts'

import { DeepPartial, useFormContext, useFormState, useWatch } from '@divvy-web/skylab.react-hook-form'
import { filter, fromPairs, is, map, pipe, toPairs } from '@divvy-web/utils'

import { useTimeoutPromptUpdater } from '@sputnik/components/TimeoutPrompt'

export interface UseFormAutoSubmitParams<T> {
  updateHandler: (data: DeepPartial<T>) => Promise<void>
}

// Map RHF's dirtyFields over the `data` received by `handleSubmit` and return the changed subset of that data.
const dirtyValues = (dirtyFields: object | boolean, allValues: object, errors: object): object | undefined => {
  // If *any* item in an array was modified, the entire array must be submitted, because there's no way to indicate
  // "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    if (errors) {
      return undefined
    }
    return allValues
  }
  // Here, we have an object
  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [key, dirtyValues(dirtyFields[key], allValues[key], errors?.[key])]),
  )
}

const deepFilterUndefinedKeys = (
  obj: Record<string | number, unknown> | Array<Record<string | number, unknown>> | unknown,
) => {
  if (is(Object)(obj) && !is(Date)(obj) && !is(Array)(obj)) {
    return pipe(
      toPairs,
      filter(([_, value]) => value !== undefined),
      map(([key, value]) => [key, deepFilterUndefinedKeys(value)]),
      fromPairs,
    )(obj)
  } else if (is(Array)(obj)) {
    return map(deepFilterUndefinedKeys, obj)
  } else {
    return obj
  }
}

const FormAutoSubmit = <T extends object>({ updateHandler }: UseFormAutoSubmitParams<T>) => {
  const { control } = useFormContext<T>()
  const { isDirty, dirtyFields, errors } = useFormState<T>({ control })
  const values = useWatch<T>({ control })

  const processUpdate = useCallback(() => {
    if (!isDirty) {
      return
    }

    const updatedFields = deepFilterUndefinedKeys(dirtyValues(dirtyFields, values, errors)) as DeepPartial<T>

    if (Object.keys(updatedFields).length) {
      updateHandler(updatedFields)
    }
  }, [dirtyFields, errors, isDirty, updateHandler, values])

  useTimeoutPromptUpdater({ updateProcessor: processUpdate })

  // saves form if user refreshes
  useEventListener('beforeunload', processUpdate)

  return <FormAutoSubmitUnmountHandler processUpdate={processUpdate} />
}

interface FormAutoSubmitProps {
  processUpdate: () => void
}

class FormAutoSubmitUnmountHandler extends React.Component<FormAutoSubmitProps> {
  constructor(props: FormAutoSubmitProps) {
    super(props)
  }

  componentWillUnmount(): void {
    this.props.processUpdate()
  }

  render(): React.ReactNode {
    return null
  }
}

export default FormAutoSubmit
